/**
 * Where a child's pictures actually live.
 *
 * The rule this file exists to enforce: **nothing is ever lost unless someone
 * deliberately deletes it**. The version this replaced broke that five ways —
 * it capped the gallery at 18 and silently dropped the oldest, it dropped the
 * oldest again whenever the quota complained, one corrupt byte took the whole
 * collection with it, a single tap deleted forever, and it never asked the
 * browser for persistent storage so Safari would evict the lot after a week.
 *
 * So: IndexedDB, one record per picture (a bad record costs one picture, not
 * all of them), no cap of any kind, every write read back before it counts as
 * saved, a quota failure that is reported instead of swallowed, and deletion
 * that only ever moves a picture to the archive. `deleteForever` is the single
 * function in the app that destroys anything.
 */

import type { SceneJson, SceneKind } from './scene.ts';

const DB_NAME = 'imagineplay';
const DB_VERSION = 1;
const STORE = 'pictures';
const LEGACY_KEY = 'imagineplay.gallery';
const LEGACY_BACKUP_KEY = 'imagineplay.gallery.v1.backup';
const PERSIST_ASKED_KEY = 'imagineplay.persist.asked';
const LOCAL_PREFIX = 'imagineplay.pic.';

export interface Picture {
  id: string;
  /** When it was first kept. */
  at: number;
  kind: SceneKind;
  /** JPEG data URL, fitted to the drawing's own bounds. */
  thumb: string;
  w: number;
  h: number;
  /** The marks themselves, so the picture can be reopened and drawn on. */
  scene: SceneJson | null;
  /** Set when put away. Null means it is on the shelf. */
  archivedAt: number | null;
}

/** Thrown when the browser refuses the write. Never swallowed. */
export class StorageFullError extends Error {
  constructor() {
    super('There is no room left to keep this picture.');
    this.name = 'StorageFullError';
  }
}

/* ---------------------------------------------------------------- backends */

/**
 * Records are plain JSON — no Blobs. That costs about a third in size versus
 * binary, which IndexedDB has room for, and buys two things worth more: the
 * same record shape works in the localStorage fallback, and an <img src> needs
 * no object-URL lifecycle to leak.
 */
export interface Backend {
  all(): Promise<Picture[]>;
  get(id: string): Promise<Picture | null>;
  put(picture: Picture): Promise<void>;
  del(id: string): Promise<void>;
}

function isPicture(v: unknown): v is Picture {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.at === 'number' &&
    typeof o.thumb === 'string' &&
    (o.kind === 'doodle' || o.kind === 'mirror') &&
    (o.archivedAt === null || typeof o.archivedAt === 'number')
  );
}

function isQuotaError(err: unknown): boolean {
  if (typeof DOMException !== 'undefined' && err instanceof DOMException) {
    return err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED';
  }
  return err instanceof Error && /quota/i.test(err.message);
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

export function idbBackend(db: IDBDatabase): Backend {
  const run = <T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const tx = db.transaction(STORE, mode);
    const out = request(fn(tx.objectStore(STORE)));
    return out.catch((err) => {
      throw isQuotaError(err) ? new StorageFullError() : err;
    });
  };

  return {
    async all() {
      const rows = await run('readonly', (s) => s.getAll() as IDBRequest<unknown[]>);
      return rows.filter(isPicture);
    },
    async get(id) {
      const row = await run('readonly', (s) => s.get(id) as IDBRequest<unknown>);
      return isPicture(row) ? row : null;
    },
    async put(picture) {
      await run('readwrite', (s) => s.put(picture));
    },
    async del(id) {
      await run('readwrite', (s) => s.delete(id));
    },
  };
}

/**
 * Fallback for browsers that refuse IndexedDB (Safari private mode is the real
 * case). One key per picture, so — exactly as in IndexedDB — a corrupted entry
 * costs that picture alone.
 */
export function localBackend(storage: Storage): Backend {
  const keyOf = (id: string) => `${LOCAL_PREFIX}${id}`;
  const write = (picture: Picture) => {
    try {
      storage.setItem(keyOf(picture.id), JSON.stringify(picture));
    } catch (err) {
      throw isQuotaError(err) ? new StorageFullError() : err;
    }
  };

  return {
    async all() {
      const out: Picture[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (!key || !key.startsWith(LOCAL_PREFIX)) continue;
        try {
          const parsed: unknown = JSON.parse(storage.getItem(key) ?? '');
          if (isPicture(parsed)) out.push(parsed);
        } catch {
          // One unreadable entry must not hide the rest.
        }
      }
      return out;
    },
    async get(id) {
      try {
        const parsed: unknown = JSON.parse(storage.getItem(keyOf(id)) ?? '');
        return isPicture(parsed) ? parsed : null;
      } catch {
        return null;
      }
    },
    async put(picture) {
      write(picture);
    },
    async del(id) {
      storage.removeItem(keyOf(id));
    },
  };
}

/** For tests, and for the impossible case where neither store is available. */
export function memoryBackend(seed: Picture[] = []): Backend {
  const map = new Map<string, Picture>(seed.map((p) => [p.id, p]));
  return {
    async all() {
      return [...map.values()];
    },
    async get(id) {
      return map.get(id) ?? null;
    },
    async put(picture) {
      map.set(picture.id, picture);
    },
    async del(id) {
      map.delete(id);
    },
  };
}

function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('no indexedDB'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB refused to open'));
    req.onblocked = () => reject(new Error('IndexedDB blocked'));
  });
}

/* ------------------------------------------------------------------- store */

export interface Store {
  list(): Promise<Picture[]>;
  save(input: NewPicture): Promise<Picture>;
  archive(id: string): Promise<void>;
  restore(id: string): Promise<void>;
  deleteForever(id: string): Promise<void>;
}

export interface NewPicture {
  kind: SceneKind;
  thumb: string;
  w: number;
  h: number;
  scene: SceneJson | null;
}

function newId(): string {
  return `art_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function createStore(backend: Backend): Store {
  /** Newest first, and archived pictures sort by when they were put away. */
  const byRecency = (a: Picture, b: Picture) => b.at - a.at;

  const mutate = async (id: string, fn: (p: Picture) => Picture): Promise<void> => {
    const current = await backend.get(id);
    if (!current) return;
    await backend.put(fn(current));
  };

  return {
    async list() {
      return (await backend.all()).sort(byRecency);
    },

    /**
     * Read the record back before reporting success. A write that silently did
     * nothing is the failure mode that loses a picture while showing "Kept it".
     */
    async save(input) {
      const picture: Picture = {
        id: newId(),
        at: Date.now(),
        kind: input.kind,
        thumb: input.thumb,
        w: input.w,
        h: input.h,
        scene: input.scene,
        archivedAt: null,
      };
      await backend.put(picture);
      const stored = await backend.get(picture.id);
      if (!stored || stored.thumb.length !== picture.thumb.length) {
        throw new Error('The picture did not survive being saved.');
      }
      return stored;
    },

    async archive(id) {
      await mutate(id, (p) => ({ ...p, archivedAt: Date.now() }));
    },

    async restore(id) {
      await mutate(id, (p) => ({ ...p, archivedAt: null }));
    },

    /** The only destructive operation in the application. */
    async deleteForever(id) {
      await backend.del(id);
    },
  };
}

/* --------------------------------------------------------------- migration */

interface LegacyArtwork {
  id: string;
  dataUrl: string;
  at: number;
}

function readLegacy(storage: Storage): LegacyArtwork[] {
  try {
    const raw = storage.getItem(LEGACY_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is LegacyArtwork => {
      if (typeof v !== 'object' || v === null) return false;
      const o = v as Record<string, unknown>;
      return typeof o.id === 'string' && typeof o.dataUrl === 'string' && typeof o.at === 'number';
    });
  } catch {
    return [];
  }
}

/**
 * Move the old localStorage gallery into the real store. The old key is
 * *renamed*, never removed, and only after every picture has been read back
 * from its new home — a migration that eats the only copy of a child's
 * drawings because the destination write half-failed is the exact accident
 * this whole file is meant to prevent.
 */
export async function migrateLegacy(backend: Backend, storage: Storage): Promise<number> {
  const legacy = readLegacy(storage);
  if (legacy.length === 0) return 0;

  const existing = new Set((await backend.all()).map((p) => p.id));
  let moved = 0;
  for (const item of legacy) {
    if (existing.has(item.id)) continue;
    await backend.put({
      id: item.id,
      at: item.at,
      kind: 'doodle',
      thumb: item.dataUrl,
      w: 0,
      h: 0,
      scene: null,
      archivedAt: null,
    });
    moved++;
  }

  for (const item of legacy) {
    if (!(await backend.get(item.id))) return moved;
  }

  storage.setItem(LEGACY_BACKUP_KEY, storage.getItem(LEGACY_KEY) ?? '');
  storage.removeItem(LEGACY_KEY);
  return moved;
}

/**
 * Ask the browser to stop evicting us. Without this, Safari clears storage for
 * a site that has not been opened in about a week — which for a drawing app a
 * child uses at the weekend means the pictures are simply gone one Saturday.
 */
export async function requestPersistence(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted?.()) return true;
    if (typeof localStorage !== 'undefined') {
      if (localStorage.getItem(PERSIST_ASKED_KEY) === 'yes') return false;
      localStorage.setItem(PERSIST_ASKED_KEY, 'yes');
    }
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

/* ---------------------------------------------------------------- singleton */

let shared: Promise<Store> | null = null;

async function build(): Promise<Store> {
  const storage = typeof localStorage === 'undefined' ? null : localStorage;
  let backend: Backend;
  try {
    backend = idbBackend(await openIdb());
  } catch {
    backend = storage ? localBackend(storage) : memoryBackend();
  }
  if (storage) {
    try {
      await migrateLegacy(backend, storage);
    } catch {
      // A failed migration leaves the legacy key untouched, so the pictures
      // are still there to try again next launch. Nothing is lost by giving up.
    }
  }
  return createStore(backend);
}

/** The application's one store. */
export function store(): Promise<Store> {
  shared = shared ?? build();
  return shared;
}
